import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MarkdownLite from "../components/MarkdownLite";
import { WhatsAppFab, WhatsAppIcon } from "../components/Extras";
import { useConfig } from "../lib/useConfig";
import { buildWhatsAppUrl } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_LABEL = { aves: "Aves", conejos: "Conejos", roedores: "Roedores" };

export default function ProductPage() {
    const { slug } = useParams();
    const config = useConfig();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [status, setStatus] = useState("loading");
    const [activePhoto, setActivePhoto] = useState(0);

    useEffect(() => {
        setStatus("loading");
        setActivePhoto(0);
        window.scrollTo(0, 0);
        axios
            .get(`${API}/products/${slug}`)
            .then(async (r) => {
                if (r.data?.error || !r.data?.slug) {
                    setStatus("notfound");
                    return;
                }
                setProduct(r.data);
                setStatus("ok");
                document.title = `${r.data.name} | LoloPets Costa Rica`;
                const meta = document.querySelector('meta[name="description"]');
                if (meta) meta.setAttribute("content", `${r.data.name} — ${r.data.line || "LoloPets"}. Disponible en las principales veterinarias y pet shops de Costa Rica.`);
                try {
                    const rel = await axios.get(`${API}/products?category=${r.data.category}`);
                    setRelated(rel.data.filter((p) => p.slug !== r.data.slug).slice(0, 4));
                } catch {}
            })
            .catch(() => setStatus("notfound"));
    }, [slug]);

    useEffect(() => {
        if (!product) return;
        const ld = document.createElement("script");        ld.type = "application/ld+json";
        ld.id = "product-jsonld";
        ld.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: product.image || undefined,
            description: product.description?.split("\n")[0] || product.name,
            brand: { "@type": "Brand", name: "LoloPets" },
            category: CATEGORY_LABEL[product.category],
        });
        document.head.appendChild(ld);
        return () => document.getElementById("product-jsonld")?.remove();
    }, [product]);

    return (
        <div data-testid="product-page" className="min-h-screen bg-white">
            <Header base="/" />
            <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
                {status === "loading" && (
                    <div data-testid="product-loading" className="grid animate-pulse gap-10 md:grid-cols-2">
                        <div className="aspect-square rounded-2xl bg-black/[0.04]" />
                        <div className="space-y-4 pt-6">
                            <div className="h-4 w-24 rounded bg-black/[0.05]" />
                            <div className="h-10 w-3/4 rounded bg-black/[0.05]" />
                            <div className="h-4 w-full rounded bg-black/[0.04]" />
                            <div className="h-4 w-5/6 rounded bg-black/[0.04]" />
                        </div>
                    </div>
                )}
                {status === "notfound" && (
                    <div data-testid="product-notfound" className="py-20 text-center">
                        <h1 className="font-display text-3xl font-bold text-brand-ink">Producto no encontrado</h1>
                        <Link to="/#productos" className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-magenta px-8 py-3 font-display font-semibold text-white">
                            <ArrowLeft className="h-4 w-4" /> Ver catálogo
                        </Link>
                    </div>
                )}
                {status === "ok" && product && (() => {
                    const photos = product.images?.length ? product.images : product.image ? [product.image] : [];
                    const current = photos[Math.min(activePhoto, photos.length - 1)];
                    return (
                    <>
                        <nav data-testid="product-breadcrumb" aria-label="Ruta" className="flex flex-wrap items-center gap-1.5 text-sm text-brand-muted">
                            <Link to="/" className="transition-colors hover:text-brand-magenta">Inicio</Link>
                            <ChevronRight className="h-3.5 w-3.5" />
                            <Link to="/#productos" className="transition-colors hover:text-brand-magenta">Productos</Link>
                            <ChevronRight className="h-3.5 w-3.5" />
                            <span className="font-medium text-brand-ink">{product.name}</span>
                        </nav>
                        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
                            <div data-testid="product-gallery">
                                <div className="overflow-hidden rounded-2xl border border-black/5 bg-black/[0.02] shadow-soft">
                                    {current ? (
                                        <img
                                            key={current}
                                            data-testid="product-image"
                                            src={current}
                                            alt={`${product.name} — LoloPets Costa Rica`}
                                            className="aspect-square w-full object-contain p-10 sm:p-14"
                                            loading="eager"
                                        />
                                    ) : (
                                        <div className="flex aspect-square items-center justify-center text-brand-muted">Sin imagen</div>
                                    )}
                                </div>
                                {photos.length > 1 && (
                                    <div data-testid="product-thumbnails" className="mt-4 flex gap-3 overflow-x-auto pb-1">
                                        {photos.map((url, i) => (
                                            <button
                                                key={`${url}-${i}`}
                                                data-testid={`product-thumb-${i}`}
                                                onClick={() => setActivePhoto(i)}
                                                aria-label={`Ver foto ${i + 1}`}
                                                aria-current={i === activePhoto}
                                                className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 bg-black/[0.02] transition-all duration-200 ${
                                                    i === activePhoto ? "border-brand-magenta shadow-soft" : "border-black/10 hover:border-brand-magenta/50"
                                                }`}
                                            >
                                                <img src={url} alt={`${product.name} foto ${i + 1}`} className="h-full w-full object-contain p-2.5" loading="lazy" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {product.line && (
                                        <span data-testid="product-line" className="rounded-full bg-brand-magenta/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-magenta">
                                            {product.line}
                                        </span>
                                    )}
                                    <span className="rounded-full bg-black/[0.05] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-ink">
                                        {CATEGORY_LABEL[product.category] || product.category}
                                    </span>
                                    {product.presentation && (
                                        <span data-testid="product-presentation" className="rounded-full bg-black/[0.05] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-ink">
                                            {product.presentation}
                                        </span>
                                    )}
                                </div>
                                <h1 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-ink sm:text-4xl">
                                    {product.name}
                                </h1>
                                <div className="mt-6">
                                    <MarkdownLite content={product.description} />
                                </div>
                                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                    <a
                                        data-testid="product-whatsapp-cta"
                                        href={buildWhatsAppUrl(config?.whatsapp, `Hola LoloPets, me interesa: ${product.name}. ¿Dónde lo puedo comprar?`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-brand-magenta px-8 py-3 font-display font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-magentadark hover:shadow-lift"
                                    >
                                        <WhatsAppIcon className="h-4 w-4" /> Consultar dónde comprar
                                    </a>
                                    <Link
                                        data-testid="product-back-catalog"
                                        to="/#productos"
                                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border-2 border-black/10 bg-white px-8 py-3 font-display font-semibold text-brand-ink transition-all duration-300 hover:border-brand-magenta/40"
                                    >
                                        <ArrowLeft className="h-4 w-4" /> Volver al catálogo
                                    </Link>
                                </div>
                                <p className="mt-5 text-sm text-brand-muted">
                                    Disponible en las principales veterinarias y pet shops del país.
                                </p>
                            </div>
                        </div>
                        {related.length > 0 && (
                            <section data-testid="related-products" className="mt-16 md:mt-24">
                                <h2 className="font-display text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">
                                    También para {CATEGORY_LABEL[product.category]?.toLowerCase()}
                                </h2>
                                <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
                                    {related.map((p) => (
                                        <Link
                                            key={p.slug}
                                            data-testid={`related-product-${p.slug}`}
                                            to={`/productos/${p.slug}`}
                                            className="group rounded-2xl border border-black/5 bg-white p-4 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                                        >
                                            <div className="overflow-hidden rounded-xl bg-black/[0.03]">
                                                {p.image && (
                                                    <img src={p.image} alt={p.name} loading="lazy" className="aspect-square w-full object-contain p-5 transition-transform duration-500 group-hover:scale-105" />
                                                )}
                                            </div>
                                            <p className="mt-3 font-display text-sm font-semibold leading-snug text-brand-ink transition-colors group-hover:text-brand-magenta">
                                                {p.name}
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                    );
                })()}
            </main>
            <Footer base="/" config={config} />
            <WhatsAppFab url={buildWhatsAppUrl(config?.whatsapp)} />
        </div>
    );
}
